import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, getShelters, getTrainers } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MapPin, Search, Heart, GraduationCap, Award, Sparkles } from "lucide-react";
import { AdoptableCard, ShelterCard, TrainerCard } from "@/components/AdoptionCards";
import { logWarn } from "@/lib/log";
import { usePageMeta } from "@/lib/usePageMeta";

export default function Adoption() {
  usePageMeta("Adopt a Dog in Australia — Shelters & Rescues | Fetchright", "Adoptable dogs available now, plus a curated directory of Australian shelters, rescues and certified trainers near your postcode.");
  const [postcode, setPostcode] = useState("");
  const [shelters, setShelters] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [adoptables, setAdoptables] = useState({ listings: [], source: "sample", note: null });
  const [state, setState] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (pc) => {
    setLoading(true);
    // Grab any recent quiz results from localStorage so the adoptable feed can
    // rank breeds this visitor matched with. Set in Results.jsx on quiz submit.
    let topBreedIds = [];
    try {
      const stored = JSON.parse(localStorage.getItem("fetchright.recentMatches") || "[]");
      if (Array.isArray(stored)) topBreedIds = stored.slice(0, 6);
    } catch (error) {
      // Corrupt/legacy localStorage entry — safe to proceed without ranking.
      logWarn("Could not parse recent matches:", error?.message);
    }

    const [sh, tr, ad] = await Promise.all([
      getShelters(pc),
      getTrainers(pc),
      axios.post(`${API}/adoptables`,
        { postcode: pc || null, top_breed_ids: topBreedIds, limit: 12 }
      ).then(r => r.data).catch(() => ({ listings: [], source: "sample", note: null })),
    ]);
    setShelters(sh.shelters);
    setState(sh.state || "");
    setTrainers(tr.trainers);
    setAdoptables(ad);
    setLoading(false);
  };

  useEffect(() => { load(""); }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-24">
      <div className="mb-10">
        <p className="text-primary font-semibold uppercase tracking-wider text-xs">Adopt, don&apos;t shop</p>
        <h1 className="font-display font-bold text-4xl md:text-5xl text-accent mt-2">
          Real Australian shelters, real dogs waiting
        </h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          A curated directory of the largest shelters and rescues across Australia. Enter your postcode to see the closest options first — every card links straight through to the shelter&apos;s live adoption page.
        </p>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); load(postcode); }}
        className="flex flex-wrap gap-3 items-center mb-10"
      >
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <MapPin size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="adoption-zip-input"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="Enter your postcode (e.g. 2000)"
            maxLength={4}
            inputMode="numeric"
            className="pl-11 pr-4 py-6 rounded-full border-border/70"
          />
        </div>
        <button
          data-testid="adoption-search-btn"
          type="submit"
          className="pill-btn inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-semibold"
        >
          <Search size={16} /> Find shelters
        </button>
        {state && (
          <span className="text-sm text-muted-foreground">
            Showing <span className="font-semibold text-accent">{state}</span> shelters first
          </span>
        )}
      </form>

      {/* Adoptable feed — real dogs available right now */}
      <section className="mb-16" data-testid="adoptable-section">
        <div className="flex items-center gap-3 mb-3">
          <Sparkles size={20} className="text-primary" />
          <h2 className="font-display font-bold text-2xl text-accent">Dogs available now</h2>
          <span className="text-sm text-muted-foreground">({adoptables.listings.length})</span>
          {adoptables.source === "petrescue" ? (
            <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-primary bg-primary/10 rounded-full px-3 py-1">
              Live · PetRescue
            </span>
          ) : (
            <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-muted-foreground bg-muted rounded-full px-3 py-1">
              Sample feed
            </span>
          )}
        </div>
        {adoptables.note && (
          <p className="text-xs text-muted-foreground mb-5 max-w-2xl">{adoptables.note}</p>
        )}

        {(() => {
          if (loading) return <div className="text-muted-foreground">Loading…</div>;
          if (adoptables.listings.length === 0) {
            return (
              <div className="rounded-3xl border-2 border-dashed border-border p-8 text-center text-muted-foreground">
                No adoptable dogs to show. Try widening your postcode search.
              </div>
            );
          }
          return (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {adoptables.listings.map((d, i) => (
                <AdoptableCard key={d.id} dog={d} index={i} />
              ))}
            </div>
          );
        })()}
      </section>

      {/* Shelters */}
      <section className="mb-16">
        <div className="flex items-center gap-3 mb-6">
          <Heart size={20} className="text-primary" />
          <h2 className="font-display font-bold text-2xl text-accent">Shelters &amp; rescues</h2>
          <span className="text-sm text-muted-foreground">({shelters.length})</span>
        </div>

        {loading ? (
          <div className="text-muted-foreground">Loading…</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {shelters.map((s, i) => (
              <ShelterCard key={s.name} shelter={s} index={i} userState={state} />
            ))}
          </div>
        )}
      </section>

      {/* Trainers */}
      <section className="mb-16">
        <div className="rounded-[2rem] bg-accent text-accent-foreground p-8 md:p-12 relative overflow-hidden">
          <div className="relative z-10 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider">
              <GraduationCap size={12} /> Pre-adoption tip
            </div>
            <h2 className="mt-5 font-display font-bold text-3xl md:text-4xl leading-tight">
              Bring a certified trainer to your first meet-and-greet
            </h2>
            <p className="mt-4 text-accent-foreground/80 text-lg">
              A one-hour trained-eye assessment can save you from months of behaviour headaches and heartbreak. Most Australian trainer networks offer &ldquo;shelter accompaniment&rdquo; as a paid service — expect $80-150 for the visit.
            </p>
          </div>
          <div className="absolute right-0 bottom-0 w-72 h-72 rounded-full bg-primary/25 blur-3xl" />
        </div>

        <div className="mt-8 grid md:grid-cols-2 gap-5">
          {trainers.map((t, i) => (
            <TrainerCard key={t.name} trainer={t} index={i} />
          ))}
        </div>
      </section>

      {/* Breeders moved to their own tab — cross-link */}
      <section>
        <div className="rounded-3xl bg-secondary p-6 md:p-8 flex flex-wrap items-center gap-4">
          <span className="w-11 h-11 rounded-full bg-primary/15 text-primary grid place-items-center shrink-0">
            <Award size={18} />
          </span>
          <div className="flex-1 min-w-[240px]">
            <div className="font-display font-bold text-lg text-accent">Prefer a specific breed from a puppy?</div>
            <div className="text-sm text-muted-foreground">
              Browse verified, ANKC-affiliated ethical breeders in the Breeders tab.
            </div>
          </div>
          <Link
            to="/breeders"
            data-testid="adopt-to-breeders-link"
            className="pill-btn inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-5 py-2.5 text-sm font-semibold"
          >
            Find a verified breeder
          </Link>
        </div>
      </section>
    </div>
  );
}
